import { lockScroll, unlockScroll } from '@theme/utilities';

const POPUP_STORAGE_KEY = 'koketa-popup-new-collection-v1';
const MESSAGE_STORAGE_KEY = 'koketa-notifications-v1';
const SOUND_STORAGE_KEY = 'koketa-notification-sound-v1';
const MESSAGE_LIFETIME = 8000;

class KoketaConversionExperience extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.popup = this.querySelector('[data-koketa-popup]');
    this.notificationRegion = this.querySelector('[data-koketa-notification-region]');
    this.cardTemplate = this.querySelector('[data-koketa-notification-card]');
    this.messages = [...this.querySelectorAll('[data-koketa-message]')].map((template) => ({
      key: template.dataset.messageKey,
      trigger: template.dataset.messageTrigger,
      title: template.dataset.messageTitle,
      text: template.dataset.messageText,
      link: template.dataset.messageLink,
      linkLabel: template.dataset.messageLinkLabel,
    }));

    this.popupEnabled = this.dataset.popupEnabled === 'true';
    this.notificationsEnabled = this.dataset.notificationsEnabled === 'true';
    this.soundEnabledByTheme = this.dataset.notificationSound === 'true';
    this.maxNotifications = Math.max(1, Number.parseInt(this.dataset.maxNotifications || '3', 10));
    this.preview = new URLSearchParams(window.location.search);
    this.previewPopup = this.preview.get('koketa_popup_preview') === '1';
    this.previewMessages = this.preview.get('koketa_notifications_preview') === '1';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.lastFocusedElement = null;
    this.notificationQueue = [];
    this.queuedKeys = new Set();
    this.currentCard = null;
    this.currentMessage = null;
    this.dismissTimer = null;
    this.dismissStartedAt = 0;
    this.dismissRemaining = MESSAGE_LIFETIME;
    this.soundContext = null;
    this.soundUnlocked = false;
    this.soundMuted = this.readStorage(SOUND_STORAGE_KEY) === 'muted';
    this.soundOnLabel = this.dataset.soundOnLabel || 'Turn on notification sound';
    this.soundOffLabel = this.dataset.soundOffLabel || 'Mute notifications';
    this.seenMessages = new Set(this.previewMessages ? [] : this.readSeenMessages());

    this.handleClick = this.handleClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.unlockSound = this.unlockSound.bind(this);
    this.checkInterfaceState = this.checkInterfaceState.bind(this);
    this.handlePrivacyChanged = this.handlePrivacyChanged.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleSectionSelect = this.handleSectionSelect.bind(this);

    this.addEventListener('click', this.handleClick);
    this.popup?.addEventListener('cancel', this.handleCancel);
    document.addEventListener('pointerdown', this.unlockSound, { once: true, capture: true });
    document.addEventListener('keydown', this.unlockSound, { once: true, capture: true });
    document.addEventListener('koketa:privacy-changed', this.handlePrivacyChanged);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);

    if (this.dataset.designMode === 'true') {
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
    }

    this.tryStart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.popup?.removeEventListener('cancel', this.handleCancel);
    document.removeEventListener('pointerdown', this.unlockSound, true);
    document.removeEventListener('keydown', this.unlockSound, true);
    document.removeEventListener('koketa:privacy-changed', this.handlePrivacyChanged);
    document.removeEventListener('shopify:section:select', this.handleSectionSelect);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.locationObserver?.disconnect();
    window.clearTimeout(this.popupTimer);
    window.clearTimeout(this.popupCloseTimer);
    window.clearTimeout(this.dismissTimer);
    window.clearTimeout(this.nextMessageTimer);
    window.clearInterval(this.interfaceTimer);
    this.resetPopup({ restoreFocus: false });
    this.dataset.initialized = 'false';
  }

  handlePrivacyChanged() {
    if (!this.popupEnabled) return;
    if (!this.previewPopup && this.readStorage(POPUP_STORAGE_KEY) === 'seen') return;
    this.attemptPopup();
  }

  handleSectionSelect(event) {
    if (event.detail?.sectionId === this.dataset.sectionId) this.showPopup(true);
  }

  handlePageHide() {
    window.clearTimeout(this.popupTimer);
    window.clearTimeout(this.popupCloseTimer);
    this.resetPopup({ restoreFocus: false });
    this.startScheduled = false;
  }

  handlePageShow(event) {
    if (event.persisted) this.tryStart();
  }

  tryStart() {
    if (this.startScheduled) return;
    this.startScheduled = true;

    const popupAlreadySeen = this.readStorage(POPUP_STORAGE_KEY) === 'seen';
    const shouldShowPopup = this.popupEnabled && (!popupAlreadySeen || this.previewPopup);
    const delay = Math.max(1, Number.parseInt(this.dataset.popupDelay || '2', 10)) * 1000;

    if (shouldShowPopup) {
      this.popupTimer = window.setTimeout(() => this.attemptPopup(), delay);
    } else {
      this.scheduleMessages(6500);
    }
  }

  attemptPopup() {
    const privacy = document.querySelector('koketa-cookie-consent[data-enabled="true"]');
    if (privacy && !document.body.classList.contains('koketa-custom-consent-ready')) {
      this.popupTimer = window.setTimeout(() => this.attemptPopup(), 250);
      return;
    }

    if (this.isInterfaceBusy({ includePopup: false })) {
      this.popupTimer = window.setTimeout(() => this.attemptPopup(), 1200);
      return;
    }
    this.showPopup(this.previewPopup);
  }

  showPopup(force = false) {
    if (!this.popup || this.popup.open || (!force && this.readStorage(POPUP_STORAGE_KEY) === 'seen')) return;

    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.popup.hidden = false;
    document.body.classList.add('koketa-conversion-popup-visible');
    lockScroll(this.popup);

    if (typeof this.popup.showModal === 'function') this.popup.showModal();
    else this.popup.setAttribute('open', '');

    window.requestAnimationFrame(() => this.popup.querySelector('[data-koketa-popup-close]')?.focus());
  }

  closePopup(reason = 'close') {
    if (!this.popup?.open) return;

    this.writeStorage(POPUP_STORAGE_KEY, 'seen');
    this.popup.classList.add('is-closing');
    const finish = () => {
      this.resetPopup({ restoreFocus: true, reason });
      document.dispatchEvent(new CustomEvent('koketa:popup-closed', { detail: { reason } }));
      this.scheduleMessages(7000);
    };

    if (this.reducedMotion) finish();
    else this.popupCloseTimer = window.setTimeout(finish, 220);
  }

  resetPopup({ restoreFocus = false, reason = 'cleanup' } = {}) {
    if (!this.popup) return;
    window.clearTimeout(this.popupCloseTimer);
    this.popup.classList.remove('is-closing');
    if (this.popup.open && typeof this.popup.close === 'function') this.popup.close(reason);
    else this.popup.removeAttribute('open');
    this.popup.hidden = true;
    document.body.classList.remove('koketa-conversion-popup-visible');
    unlockScroll(this.popup);
    if (restoreFocus) this.lastFocusedElement?.focus?.({ preventScroll: true });
    this.lastFocusedElement = null;
  }

  handleCancel(event) {
    event.preventDefault();
    this.closePopup('escape');
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;

    if (target.closest('[data-koketa-popup-close]')) {
      this.closePopup('close');
      return;
    }

    const popupPrimary = target.closest('[data-koketa-popup-primary]');
    if (popupPrimary) {
      event.preventDefault();
      this.writeStorage(POPUP_STORAGE_KEY, 'seen');
      const destination = popupPrimary.href;
      this.resetPopup({ restoreFocus: false, reason: 'primary' });
      window.location.assign(destination);
      return;
    }

    if (target.closest('[data-koketa-message-close]')) {
      this.dismissNotification('right');
      return;
    }

    const soundToggle = target.closest('[data-koketa-sound-toggle]');
    if (soundToggle) {
      this.toggleSound(soundToggle);
    }
  }

  scheduleMessages(initialDelay = 7000) {
    if (!this.notificationsEnabled || this.messagesScheduled) return;
    this.messagesScheduled = true;

    this.messages.forEach((message) => {
      if (message.trigger === 'after_popup') {
        window.setTimeout(() => this.enqueueMessage(message), initialDelay);
      }

      if (message.trigger === 'after_scroll') this.setupScrollMessage(message);
      if (message.trigger === 'location') this.setupLocationMessage(message);
    });
  }

  setupScrollMessage(message) {
    let queued = false;
    const pageWrapper = document.querySelector('.page-wrapper');
    const scrollRoot = window.matchMedia('(min-width: 990px)').matches && pageWrapper ? pageWrapper : window;
    const queue = () => {
      if (queued) return;
      const current = scrollRoot === window ? window.scrollY : scrollRoot.scrollTop;
      const scrollHeight = scrollRoot === window ? document.documentElement.scrollHeight : scrollRoot.scrollHeight;
      const viewportHeight = scrollRoot === window ? window.innerHeight : scrollRoot.clientHeight;
      const scrollable = Math.max(1, scrollHeight - viewportHeight);
      if (current / scrollable < .22) return;
      queued = true;
      scrollRoot.removeEventListener('scroll', queue);
      window.setTimeout(() => this.enqueueMessage(message), 2200);
    };

    scrollRoot.addEventListener('scroll', queue, { passive: true });
    window.setTimeout(() => {
      if (queued) return;
      queued = true;
      scrollRoot.removeEventListener('scroll', queue);
      this.enqueueMessage(message);
    }, 32000);
  }

  setupLocationMessage(message) {
    const location = document.querySelector('#contacto');
    if (!location || !('IntersectionObserver' in window)) {
      window.setTimeout(() => this.enqueueMessage(message), 55000);
      return;
    }

    this.locationObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.locationObserver.disconnect();
      window.setTimeout(() => this.enqueueMessage(message), 900);
    }, { rootMargin: '10% 0px 10%', threshold: .08 });
    this.locationObserver.observe(location);
  }

  enqueueMessage(message) {
    if (!message?.key || this.seenMessages.has(message.key) || this.queuedKeys.has(message.key)) return;
    if (this.seenMessages.size >= this.maxNotifications && !this.previewMessages) return;

    this.queuedKeys.add(message.key);
    this.notificationQueue.push(message);
    this.processQueue();
  }

  processQueue() {
    if (this.currentCard || !this.notificationQueue.length) return;
    if (this.isInterfaceBusy({ includePopup: true })) {
      this.nextMessageTimer = window.setTimeout(() => this.processQueue(), 1400);
      return;
    }

    const message = this.notificationQueue.shift();
    this.queuedKeys.delete(message.key);
    this.showNotification(message);
  }

  showNotification(message) {
    const fragment = this.cardTemplate?.content.cloneNode(true);
    const card = fragment?.querySelector('[data-koketa-message-card]');
    if (!card || !this.notificationRegion) return;

    card.dataset.messageKey = message.key;
    card.querySelector('[data-koketa-message-title]').textContent = message.title;
    card.querySelector('[data-koketa-message-text]').textContent = message.text;
    const link = card.querySelector('[data-koketa-message-link]');
    link.href = message.link;
    link.querySelector('[data-koketa-message-link-label]').textContent = message.linkLabel;

    this.currentCard = card;
    this.currentMessage = message;
    this.dismissRemaining = MESSAGE_LIFETIME;
    this.notificationRegion.replaceChildren(card);
    this.updateSoundButton(card);
    this.bindNotificationInteractions(card);

    this.seenMessages.add(message.key);
    this.writeSeenMessages();
    this.playArrivalSound();
    this.startDismissTimer();
    this.interfaceTimer = window.setInterval(this.checkInterfaceState, 400);
  }

  bindNotificationInteractions(card) {
    card.addEventListener('mouseenter', () => this.pauseDismissTimer());
    card.addEventListener('mouseleave', () => this.resumeDismissTimer());
    card.addEventListener('focusin', () => this.pauseDismissTimer());
    card.addEventListener('focusout', (event) => {
      if (!card.contains(event.relatedTarget)) this.resumeDismissTimer();
    });

    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let dragging = false;

    card.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('a, button')) return;
      startX = event.clientX;
      startY = event.clientY;
      deltaX = 0;
      dragging = false;
      card.setPointerCapture?.(event.pointerId);
      this.pauseDismissTimer();
    });

    card.addEventListener('pointermove', (event) => {
      if (!card.hasPointerCapture?.(event.pointerId)) return;
      const nextX = event.clientX - startX;
      const nextY = event.clientY - startY;
      if (!dragging && Math.abs(nextX) > 8 && Math.abs(nextX) > Math.abs(nextY) * 1.2) dragging = true;
      if (!dragging) return;
      event.preventDefault();
      deltaX = nextX;
      card.style.transform = `translateX(${deltaX}px) rotate(${deltaX * .012}deg)`;
      card.style.opacity = String(Math.max(.35, 1 - Math.abs(deltaX) / card.offsetWidth));
    });

    const finishDrag = (event) => {
      if (!card.hasPointerCapture?.(event.pointerId)) return;
      card.releasePointerCapture?.(event.pointerId);
      const threshold = Math.min(110, card.offsetWidth * .28);
      if (dragging && Math.abs(deltaX) >= threshold) {
        this.dismissNotification(deltaX < 0 ? 'left' : 'right');
        return;
      }
      card.classList.add('is-resetting');
      card.style.removeProperty('transform');
      card.style.removeProperty('opacity');
      window.setTimeout(() => card.classList.remove('is-resetting'), 190);
      this.resumeDismissTimer();
    };

    card.addEventListener('pointerup', finishDrag);
    card.addEventListener('pointercancel', finishDrag);
  }

  startDismissTimer() {
    window.clearTimeout(this.dismissTimer);
    this.dismissStartedAt = performance.now();
    this.currentCard?.classList.remove('is-paused');
    this.dismissTimer = window.setTimeout(() => this.dismissNotification('right'), this.dismissRemaining);
  }

  pauseDismissTimer() {
    if (!this.currentCard || !this.dismissTimer) return;
    window.clearTimeout(this.dismissTimer);
    this.dismissTimer = null;
    this.dismissRemaining = Math.max(350, this.dismissRemaining - (performance.now() - this.dismissStartedAt));
    this.currentCard.classList.add('is-paused');
  }

  resumeDismissTimer() {
    if (!this.currentCard || this.dismissTimer || this.currentCard.classList.contains('is-suspended')) return;
    this.startDismissTimer();
  }

  dismissNotification(direction = 'right') {
    const card = this.currentCard;
    if (!card) return;

    window.clearTimeout(this.dismissTimer);
    window.clearInterval(this.interfaceTimer);
    this.dismissTimer = null;
    card.classList.add(direction === 'left' ? 'is-exiting-left' : 'is-exiting-right');

    const finish = () => {
      card.remove();
      this.currentCard = null;
      this.currentMessage = null;
      this.nextMessageTimer = window.setTimeout(() => this.processQueue(), 6500);
    };

    if (this.reducedMotion) finish();
    else window.setTimeout(finish, 190);
  }

  checkInterfaceState() {
    if (!this.currentCard) return;
    const blocked = this.isInterfaceBusy({ includePopup: true });
    this.currentCard.classList.toggle('is-suspended', blocked);
    if (blocked) this.pauseDismissTimer();
    else this.resumeDismissTimer();
  }

  isInterfaceBusy({ includePopup }) {
    if (document.body.classList.contains('koketa-cookie-banner-visible')) return true;
    if (document.querySelector('.koketa-cookie__banner:not([hidden]), .koketa-cookie__preferences[open]')) return true;
    if (document.querySelector('koketa-cookie-consent dialog[open]')) return true;
    if (document.querySelector('#Details-menu-drawer-container[open], #Details-menu-drawer-container.menu-open')) return true;
    if (document.querySelector('#cart-drawer[open], #cart-drawer .theme-drawer__dialog[open], [data-whatsapp-panel]:not([hidden])')) return true;
    if (document.querySelector('#KoketaWishlist[open], #search-modal dialog[open], #quick-add-dialog dialog[open]')) return true;
    if (document.documentElement.hasAttribute('scroll-lock') && !this.popup?.open) return true;
    if (includePopup && this.popup?.open) return true;
    return Boolean(document.querySelector('dialog[open]:not([data-koketa-popup]):not(.koketa-cookie__preferences)'));
  }

  unlockSound() {
    if (!this.soundEnabledByTheme || this.soundMuted) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    if (!this.soundContext) this.soundContext = new AudioContext();
    this.soundContext.resume?.();
    this.soundUnlocked = true;
  }

  playArrivalSound() {
    if (!this.soundEnabledByTheme || this.soundMuted || !this.soundUnlocked || !this.soundContext || document.visibilityState !== 'visible') return;
    if (this.soundContext.state !== 'running') return;

    const now = this.soundContext.currentTime;
    const gain = this.soundContext.createGain();
    gain.gain.setValueAtTime(.0001, now);
    gain.gain.exponentialRampToValueAtTime(.035, now + .014);
    gain.gain.exponentialRampToValueAtTime(.0001, now + .32);
    gain.connect(this.soundContext.destination);

    const first = this.soundContext.createOscillator();
    first.type = 'sine';
    first.frequency.setValueAtTime(659.25, now);
    first.connect(gain);
    first.start(now);
    first.stop(now + .18);

    const second = this.soundContext.createOscillator();
    second.type = 'sine';
    second.frequency.setValueAtTime(987.77, now + .085);
    second.connect(gain);
    second.start(now + .085);
    second.stop(now + .32);
  }

  toggleSound(button) {
    this.soundMuted = !this.soundMuted;
    this.writeStorage(SOUND_STORAGE_KEY, this.soundMuted ? 'muted' : 'enabled');
    this.unlockSound();
    this.updateSoundButton(this.currentCard);
    if (!this.soundMuted) this.playArrivalSound();
    button.focus({ preventScroll: true });
  }

  updateSoundButton(card) {
    if (!card) return;
    card.classList.toggle('is-muted', this.soundMuted || !this.soundEnabledByTheme);
    const button = card.querySelector('[data-koketa-sound-toggle]');
    if (!button) return;
    button.hidden = !this.soundEnabledByTheme;
    button.setAttribute('aria-pressed', String(this.soundMuted));
    button.setAttribute('aria-label', this.soundMuted ? this.soundOnLabel : this.soundOffLabel);
  }

  readSeenMessages() {
    try {
      const value = JSON.parse(sessionStorage.getItem(MESSAGE_STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_error) {
      return [];
    }
  }

  writeSeenMessages() {
    try {
      sessionStorage.setItem(MESSAGE_STORAGE_KEY, JSON.stringify([...this.seenMessages]));
    } catch (_error) {
      /* Browsing still works if storage is unavailable. */
    }
  }

  readStorage(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_error) {
      /* Browsing still works if storage is unavailable. */
    }
  }
}

if (!customElements.get('koketa-conversion-experience')) {
  customElements.define('koketa-conversion-experience', KoketaConversionExperience);
}
